import React, { useState } from "react";
import axios from "axios";
import { API } from "@/auth";

/**
 * Mobile-friendly one-page upload for the homepage halftime cypher MP4.
 * No login required — passcode-gated. Streams the file to the backend,
 * which pushes it to persistent Object Storage without re-encoding.
 */
export default function UploadHero() {
 const [passcode, setPasscode] = useState("");
 const [version, setVersion] = useState(2);
 const [title, setTitle] = useState("HALFTIME CYPHER · IRON VERSE vs THE ARCHITECT");
 const [file, setFile] = useState(null);
 const [progress, setProgress] = useState(0);
 const [busy, setBusy] = useState(false);
 const [error, setError] = useState(null);
 const [result, setResult] = useState(null);

 const onFileChange = (e) => {
 setError(null);
 setResult(null);
 setFile(e.target.files?.[0] || null);
 };

 const onSubmit = async (e) => {
 e.preventDefault();
 setError(null); setResult(null); setProgress(0);
 if (!file) { setError("Pick a file first"); return; }
 if (!passcode) { setError("Enter the passcode"); return; }
 if (file.size > 100 * 1024 * 1024) { setError("File exceeds 100 MB cap"); return; }
 setBusy(true);
 try {
 const fd = new FormData();
 fd.append("passcode", passcode);
 fd.append("version", String(version));
 fd.append("title", title);
 fd.append("video", file, file.name);
 const res = await axios.post(`${API}/upload-hero`, fd, {
 headers: { "Content-Type": "multipart/form-data" },
 maxContentLength: Infinity,
 maxBodyLength: Infinity,
 timeout: 600000, // 10 min for slow phone uploads
 onUploadProgress: (evt) => {
 if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
 },
 });
 setResult(res.data);
 } catch (err) {
 setError(err?.response?.data?.detail || err?.message || "Upload failed");
 } finally {
 setBusy(false);
 }
 };

 return (
 <div className="min-h-screen bg-black text-white flex items-start justify-center py-10 px-4">
 <div className="w-full max-w-lg">
 <h1 className="font-display text-3xl md:text-4xl tracking-tight text-yt-yellow mb-1" data-testid="upload-hero-title">
 UPLOAD HOMEPAGE CYPHER
 </h1>
 <p className="font-mono text-xs tracking-widest text-yt-muted mb-6">
 hero/cypher_v&lt;version&gt;.mp4 · persistent storage
 </p>

 <form onSubmit={onSubmit} className="space-y-4">
 <label className="block">
 <span className="font-mono text-[10px] tracking-[0.3em] text-yt-muted">PASSCODE</span>
 <input type="password" value={passcode} onChange={(e) => setPasscode(e.target.value)}
 data-testid="upload-hero-passcode"
 className="mt-1 w-full bg-yt-surface border-2 border-yt-border focus:border-yt-yellow px-4 py-3 text-white font-mono tracking-widest outline-none"
 placeholder="ROMANO21" autoComplete="off" />
 </label>

 <label className="block">
 <span className="font-mono text-[10px] tracking-[0.3em] text-yt-muted">VERSION #</span>
 <input type="number" value={version} min={1} max={999} onChange={(e) => setVersion(parseInt(e.target.value || "2"))}
 data-testid="upload-hero-version"
 className="mt-1 w-full bg-yt-surface border-2 border-yt-border focus:border-yt-yellow px-4 py-3 text-white font-mono outline-none" />
 </label>

 <label className="block">
 <span className="font-mono text-[10px] tracking-[0.3em] text-yt-muted">TITLE</span>
 <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
 data-testid="upload-hero-title-input"
 className="mt-1 w-full bg-yt-surface border-2 border-yt-border focus:border-yt-yellow px-4 py-3 text-white font-mono outline-none" />
 </label>

 <label className="block">
 <span className="font-mono text-[10px] tracking-[0.3em] text-yt-muted">MP4 FILE (max 100 MB)</span>
 <input type="file" accept="video/mp4" onChange={onFileChange}
 data-testid="upload-hero-file"
 className="mt-1 w-full bg-yt-surface border-2 border-yt-border px-3 py-3 text-white font-mono" />
 {file && (
 <div className="mt-1 font-mono text-[11px] text-yt-muted">
 {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB
 </div>
 )}
 </label>

 <button type="submit" disabled={busy}
 data-testid="upload-hero-submit"
 className="w-full py-4 font-display text-xl tracking-wider bg-yt-yellow text-black border-2 border-yt-yellow hover:bg-black hover:text-yt-yellow transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
 {busy ? `UPLOADING… ${progress}%` : "UPLOAD"}
 </button>

 {busy && (
 <div className="w-full h-2 bg-yt-surface border border-yt-border" data-testid="upload-hero-progress">
 <div className="h-full bg-yt-yellow transition-all duration-200" style={{ width: `${progress}%` }} />
 </div>
 )}

 {error && (
 <div className="border-2 border-yt-red bg-yt-red/10 text-yt-red px-4 py-3 font-mono text-sm" data-testid="upload-hero-error">
 {String(error)}
 </div>
 )}

 {result && (
 <div className="border-2 border-yt-yellow bg-yt-yellow/5 px-4 py-3 space-y-1 font-mono text-xs" data-testid="upload-hero-result">
 <div className="text-yt-yellow tracking-widest">✓ UPLOAD OK</div>
 <div>video_key: {result.video_key}</div>
 <div>poster_key: {result.poster_key || "(none)"}</div>
 <div>size: {result.size_mb} MB</div>
 <div>version: v{result.version}</div>
 <div className="pt-2">
 <a href={result.video_url} className="text-yt-yellow underline">▶ Preview MP4</a>
 </div>
 <div className="pt-2 text-yt-muted">
 Homepage will pick this up on next load. Click <b>Deploy</b> in the dashboard to push to youngtongues.com.
 </div>
 </div>
 )}
 </form>
 </div>
 </div>
 );
}
